import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@components/atoms/button';
import { Typography } from '@components/atoms/typography';
import { CloseCircleIcon } from '@components/icons';
import type { ThemeProps } from '@models/types';
import type { ModelCheckError } from '@modules/dashboard/pages/kyc/docs-verification/docs-verification-page/tabs/selfie/liveness/step/liveness-process/hints/types';
import { errorTranslationByType } from '@modules/dashboard/pages/kyc/docs-verification/docs-verification-page/tabs/selfie/liveness/step/liveness-process/hooks/use-liveness-error-text';
import { useTheme } from '@mui/material';
import Box from '@mui/material/Box';
import Dialog from '@mui/material/Dialog';
import Stack from '@mui/material/Stack';

type ErrorModalProps = {
    error?: ModelCheckError;
    onCheckRecommendations: () => void;
    onClose: () => void;
    onTryAgain: () => void;
};

export const ErrorModal: React.FC<ErrorModalProps> = ({
    error,
    onCheckRecommendations,
    onClose,
    onTryAgain,
}) => {
    const { t } = useTranslation();
    const theme = useTheme<ThemeProps>();

    const title = useMemo(() => {
        if (error) {
            const text = t(errorTranslationByType[error]);

            return text.split('.')[0];
        }

        return '';
    }, [error, t]);

    return (
        <Dialog
            open={!!error}
            onClose={onClose}
            fullWidth={true}
            maxWidth={false}
            slotProps={{
                paper: {
                    sx: {
                        width: 'calc(100% - 32px)',
                        maxWidth: '368px',
                        margin: '16px',
                        borderRadius: '30px',
                        boxShadow: '0px 14px 54px 0px rgba(0, 0, 0, 0.25)',
                        backgroundColor: theme.palette.secondaryA5,
                        overflow: 'visible',
                    },
                },
            }}
        >
            <Stack sx={{ position: 'relative', px: 3, pt: 3, pb: 4, alignItems: 'center' }}>
                <CloseCircleIcon
                    onClick={onClose}
                    sx={{
                        position: 'absolute',
                        top: 22,
                        right: 22,
                        width: 24,
                        height: 24,
                        color: theme.palette.secondaryA8,
                        transition: 'all 0.3s',
                        cursor: 'pointer',
                        '&:hover': {
                            opacity: 0.5,
                        },
                    }}
                />
                <Box sx={{ width: '100%', maxWidth: '304px', pt: 4.5 }}>
                    <Typography tag='h3' variant='h3' sx={{ textAlign: 'center', mb: 2 }}>
                        {title}
                    </Typography>
                    <Typography tag='p' variant='small-text-bold'>
                        {t('docVerificationWidget.liveness.errorModal.description', {
                            defaultValue: 'Check the recommendations before trying again.',
                        })}
                    </Typography>
                </Box>
                <Stack sx={{ width: '100%', mt: 2.75, gap: 2.75 }}>
                    <Button
                        variant='primary'
                        size='xl'
                        fullWidth={true}
                        onClick={onCheckRecommendations}
                    >
                        {t('docVerificationWidget.liveness.errorModal.checkRecommendations', {
                            defaultValue: 'Check recommendations',
                        })}
                    </Button>
                    <Button variant='ghost' onClick={onTryAgain}>
                        {t('common.tryAgain')}
                    </Button>
                </Stack>
            </Stack>
        </Dialog>
    );
};
